import { useEffect, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useConfig } from '@lore/config';

/**
 * @typedef {Object} ProjectMetricItem
 * @property {number} id
 * @property {string} title
 * @property {number} networkId
 * @property {number} siteId
 * @property {string} siteName
 * @property {string} activityName
 * @property {number} students
 * @property {number|null} actualStudents - creator's evaluation count when it qualifies, else `students` (computed by steward-api)
 * @property {string} startDate
 * @property {string} endDate
 * @property {string} createdAt
 * @property {number} numberOfQuestionsPending
 * @property {number} numberOfQuestionsConnect
 * @property {number} numberOfQuestionsDecline
 * @property {boolean} completed
 * @property {boolean} active
 * @property {boolean} expired
 * @property {boolean} cancelled
 * @property {string[]} subjectNames
 */

/**
 * @typedef {Object} ProjectMetricsResponse
 * @property {number} total
 * @property {number} page
 * @property {number} pageSize
 * @property {string|null} startDate
 * @property {string|null} endDate
 * @property {ProjectMetricItem[]} data
 */

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {ProjectMetricItem}
 */
export const _ProjectMetricItem = {};

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {ProjectMetricsResponse}
 */
export const _ProjectMetricsResponse = {};

const STALE_TIME = 24 * 60 * 60 * 1000; // 24 hours

const EMULATE_SINGLE_SITE = false;

/**
 * Filter the data to find the site with the most events and only return data for that site. Useful only for debugging
 * and testing.
 *
 * @param {ProjectMetricsResponse} response
 * @returns {ProjectMetricsResponse}
 */
function emulateSingleSiteData(response) {
  if (EMULATE_SINGLE_SITE && response.data && response.data.length > 0) {
    const siteCounts = response.data.reduce((acc, item) => {
      const siteId = item.siteId;
      acc[siteId] = (acc[siteId] || 0) + 1;
      return acc;
    }, {});

    let maxSiteId = null;
    let maxCount = -1;

    for (const siteId in siteCounts) {
      if (siteCounts[siteId] > maxCount) {
        maxCount = siteCounts[siteId];
        maxSiteId = Number(siteId);
      }
    }

    const filteredData = response.data.filter((item) => item.siteId === maxSiteId);

    return {
      ...response,
      data: filteredData,
      total: filteredData.length,
    };
  }

  return response;
}

/**
 * Custom hook to fetch project metrics from the core API.
 *
 * @param {Object} [params={}] - Query parameters for the request.
 * @param {number|string} [params.networkId] - The ID of the network.
 * @param {string} [params.startDate] - Optional ISO date string for the start of the range.
 * @param {string} [params.endDate] - Optional ISO date string for the end of the range.
 * @param {number} [params.pageSize=500] - The number of items per page.
 * @param {Object} [options={}] - Configuration options like 'enabled' to control the query.
 * @returns {import('@tanstack/react-query').UseInfiniteQueryResult<ProjectMetricsResponse> & {data: ProjectMetricItem[]}} React Query result object.
 */
export default function useCoreProjectsMetrics(
  {networkId, startDate, endDate, pageSize = 500} = {},
  {enabled = true} = {}
) {
  const config = useConfig();
  const {apiRoot, headers} = config.connections.default;

  const query = useInfiniteQuery({
    queryKey: ['project', 'core', 'metrics', {networkId, startDate, endDate, pageSize}],

    queryFn: ({pageParam = 1}) => {
      const url = new URL(`${apiRoot}/core/projects/metrics`);

      if (networkId) url.searchParams.append('networkId', String(networkId));
      if (startDate) url.searchParams.append('startDate', startDate);
      if (endDate) url.searchParams.append('endDate', endDate);
      url.searchParams.append('page', String(pageParam));
      url.searchParams.append('pageSize', String(pageSize));

      return fetch(url.toString(), {
        method: 'GET',
        headers: headers(),
      }).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch project metrics');
        }
        return res.json();
      }).then(emulateSingleSiteData);
    },

    getNextPageParam: (lastPage) => {
      const {page, pageSize, total} = lastPage;
      const totalPages = Math.ceil(total / pageSize);
      return page < totalPages ? page + 1 : undefined;
    },

    staleTime: STALE_TIME,
    enabled: enabled,
  });

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
  } = query;

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && enabled) {
      fetchNextPage().catch(() => console.error('Failed to fetch next page of metrics'));
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, enabled]);

  const allData = useMemo(() => {
    return data ? data.pages.flatMap((page) => page.data) : [];
  }, [data]);

  return {
    ...query,
    data: allData,
    isLoading: isLoading || isFetchingNextPage || hasNextPage,
  };
}
